import { useState } from 'react';
import type { ReactElement } from 'react';
import type { ZuruecksetzenErgebnis } from '@mcikm-vote/shared';

import { useSystemZuruecksetzen } from '../../admin/systemApi';
import { BestaetigungsDialog } from '../../components/BestaetigungsDialog';
import { Feld } from '../../components/Feld';
import { Fehlermeldung } from '../../components/Fehlermeldung';
import { Karte } from '../../components/Karte';
import { Knopf } from '../../components/Knopf';

/**
 * Vollständiges Zurücksetzen des Systems (Änderungsanforderung ÄA-3).
 *
 * Gedacht für die Übergabe an den nächsten Nutzer: Dieselbe Installation wird nacheinander von
 * verschiedenen Vereinen, Parteien und Organisationen verwendet, und vom Vorgänger darf nichts
 * übrig bleiben.
 *
 * **Zwei Schranken, und beide sind Absicht.** Der 24-stellige Schlüssel steht in der `.env` auf dem
 * Server — wer ihn nicht hat, kommt hier nicht weiter, auch nicht mit einer Adminsitzung. Und der
 * Bestätigungsdialog davor zählt noch einmal auf, was gleich verschwindet. Beides zusammen macht
 * das Versehen unwahrscheinlich; gegen einen Entschlossenen mit Serverzugang schützt nichts davon,
 * und das ist auch nicht der Zweck.
 *
 * Der Abschnitt erscheint nur, wenn der Schlüssel eingerichtet ist. Eine sichtbare, aber
 * unbenutzbare Schaltfläche wäre eine Einladung zum Ausprobieren.
 */
export function ZuruecksetzenAbschnitt(): ReactElement {
  const [schluessel, setSchluessel] = useState('');
  const [fragt, setFragt] = useState(false);
  const [ergebnis, setErgebnis] = useState<ZuruecksetzenErgebnis | null>(null);

  const zuruecksetzen = useSystemZuruecksetzen();

  function fuehreAus(): void {
    zuruecksetzen.mutate(schluessel, {
      onSuccess: (antwort) => {
        setErgebnis(antwort);
        setFragt(false);
        // Der Schlüssel bleibt nicht im Formular stehen: Ein zweiter Klick soll ihn erneut
        // verlangen, und im Browser soll er nicht länger liegen als nötig.
        setSchluessel('');
      },
      onError: () => {
        setFragt(false);
      },
    });
  }

  return (
    <Karte>
      <h2 className="text-base font-semibold">System zurücksetzen</h2>

      <p className="border-fehler/50 bg-fehler/10 mt-3 rounded-md border p-3 text-sm">
        <strong>Dieser Vorgang löscht den gesamten Bestand und ist nicht umkehrbar.</strong>{' '}
        Veranstaltungen, Abstimmungen, Ergebnisse, Tokens und die Sperrliste der
        Veranstaltungscodes, alle Exportdateien, alle Backups und Sicherungsstände, die technischen
        Protokolle und das vollständige Änderungsprotokoll. Es gibt danach keinen Weg zurück — ein
        Backup wird <strong>nicht</strong> erzeugt, denn es würde mitgelöscht.
      </p>

      <p className="text-text-gedaempft mt-3 text-sm">
        Bestehen bleiben allein der Administratorzugang, die Schlüssel aus der <code>.env</code> und
        Ihre laufende Anmeldung. Die Anwendung ist unmittelbar danach wieder einsatzbereit; ein
        Neustart ist nicht nötig.
      </p>

      {ergebnis !== null && (
        <p
          role="status"
          className="border-rand bg-flaeche-gedaempft mt-3 rounded-md border p-3 text-sm"
        >
          Das System wurde zurückgesetzt: {ergebnis.zeilen} Datensätze, {ergebnis.dateien.exporte}{' '}
          Exportdatei(en), {ergebnis.dateien.backups} Backupdatei(en) und{' '}
          {ergebnis.dateien.protokolle} Protokolldatei(en) gelöscht, {ergebnis.beendeteSitzungen}{' '}
          Teilnehmersitzung(en) beendet.
          {ergebnis.nichtGeloescht > 0 && (
            <>
              {' '}
              <strong>{ergebnis.nichtGeloescht} Datei(en) ließen sich nicht löschen</strong> und
              sind auf dem Server von Hand zu entfernen.
            </>
          )}
        </p>
      )}

      <div className="mt-4 max-w-sm">
        <Feld
          name="reset-schluessel"
          beschriftung="Schlüssel zum Zurücksetzen"
          typ="password"
          wert={schluessel}
          onAendern={setSchluessel}
          hinweis="Die 24 Zeichen aus SYSTEM_RESET_KEY in der .env dieser Installation."
        />
      </div>

      <div className="mt-4">
        <Knopf
          art="gefahr"
          disabled={schluessel === '' || zuruecksetzen.isPending}
          onClick={() => {
            setFragt(true);
          }}
        >
          Alles unwiderruflich löschen
        </Knopf>
      </div>

      {zuruecksetzen.isError && <Fehlermeldung>{zuruecksetzen.error.message}</Fehlermeldung>}

      {fragt && (
        <BestaetigungsDialog
          titel="Gesamten Bestand löschen?"
          bestaetigungstext="Endgültig löschen"
          gefahr
          laeuft={zuruecksetzen.isPending}
          beschreibung={
            <>
              <p>
                Alle Veranstaltungen, Abstimmungen, Ergebnisse und Tokens werden gelöscht, ebenso
                sämtliche Exportdateien, Backups, Sicherungsstände, technischen Protokolle und das
                vollständige Änderungsprotokoll.
              </p>
              <p className="mt-2">
                Es wird kein Backup erzeugt. Der Vorgang lässt sich nicht rückgängig machen.
              </p>
            </>
          }
          onBestaetigen={fuehreAus}
          onAbbrechen={() => {
            setFragt(false);
          }}
        />
      )}
    </Karte>
  );
}
